import React, { useState } from 'react';
import { classes, useSlug } from '@bloomberg/media-ui-core';
import { GlobalProps } from '@bloomberg/media-ui-core/types';
import { Triangle, UserAccountProfile } from '@bloomberg/media-ui-icons';
import { Popover } from '@bloomberg/media-ui-overlay';
import { default as FocusTrap } from 'focus-trap-react';

import {
  DEFAULT_USER_OPTIONS_BUTTON_TEXT,
  USER_OPTIONS_MENU_ITEMS_SUBSCRIBER,
  USER_OPTIONS_MENU_ITEMS_SUBSCRIBER_WITH_HUB_ACCESS,
} from '../constants/constants';
import { NavTips, SubscriptionData, THEMES, User } from '../types';
import { UserOptionsDropdown } from '../UserOptionsDropdown';
import { getLocalizedMenuSections } from '../utils/getLocalizedMenuSections';
import { useTranslateMessage } from '../utils/translate-messages';

import styles from './UserOptions.module.css';

export type UserOptionsProps = GlobalProps & {
  isDropdownOpen?: boolean;
  setIsDropdownOpen?: (isOpen: boolean) => void;
  user: User;
  theme?: THEMES;
  tips?: NavTips;
  onSignOut: () => void;
};

export type UserOptionsButtonProps = GlobalProps & {
  text: string;
  onClick: () => void;
  theme?: THEMES;
  children?: React.ReactNode;
  icon?: React.ReactNode;
};

export const UserOptionsButton = React.forwardRef<
  HTMLDivElement,
  UserOptionsButtonProps
>(function UserOptionsButtonComponent(props: UserOptionsButtonProps, ref) {
  const { text, onClick, children, icon } = props;
  const translateMessage = useTranslateMessage();

  return (
    <div
      className={styles.userOptionsButtonContainer}
      ref={ref}
      aria-label={translateMessage({
        id: 'media-ui-nav_arialabel_userAccountButtonContainer',
        defaultMessage: 'User Account Button Container',
      })}
    >
      <button
        onClick={onClick}
        className={styles.userAccountProfileButton}
        aria-label={translateMessage({
          id: 'media-ui-nav_arialabel_userAccountButton',
          defaultMessage: 'User Account Button',
        })}
        data-testid="user-options-button"
        role="button"
      >
        {icon}
        <p className={styles.userOptionsButtonText}>{text}</p>
        {children}
      </button>
    </div>
  );
});

export const UserOptions = React.forwardRef<HTMLDivElement, UserOptionsProps>(
  function UserOptionsComponent(props: UserOptionsProps, ref) {
    const {
      isDropdownOpen,
      setIsDropdownOpen = () => {},
      user,
      theme = THEMES.LIGHT,
      onSignOut,
      tips,
    } = props;

    const [userOptionsButton, setUserOptionsButton] = useState<Element | null>(
      null
    );
    const { firstName, subscriptionsData = [] } = user;
    const showCoporateHubLink = subscriptionsData.some(
      (sub: SubscriptionData) => sub.corporateHubAccess
    );
    const translateMessage = useTranslateMessage();

    const onUserOptionSelect = (url: string) => {
      window.open(url, '_self');
    };

    const componentName = useSlug('user-options');

    const cx = classes(
      [
        styles.userOptionsContainer,
        {
          [styles.light]: theme === THEMES.LIGHT,
          [styles.dark]: theme === THEMES.DARK,
          [styles.blue]: theme === THEMES.BLUE,
        },
      ],
      {
        ...props,
      }
    );

    const reset = () => {
      if (tips?.corporateHub) {
        tips?.corporateHub.onDismiss();
      }
    };

    const closeUserOptions = () => {
      setIsDropdownOpen(false);

      return true;
    };

    const openUserOptions = () => {
      reset();
      setIsDropdownOpen(true);
    };

    const onUserButtonClick = () => {
      return !isDropdownOpen ? openUserOptions() : closeUserOptions();
    };

    const getDropdownSections = () => {
      if (user.isSubscriber && showCoporateHubLink) {
        return USER_OPTIONS_MENU_ITEMS_SUBSCRIBER_WITH_HUB_ACCESS;
      }

      if (user.isSubscriber) {
        return USER_OPTIONS_MENU_ITEMS_SUBSCRIBER;
      }

      return USER_OPTIONS_MENU_ITEMS_SUBSCRIBER;
    };

    return (
      <FocusTrap
        active={isDropdownOpen}
        focusTrapOptions={{
          fallbackFocus: `.${styles.userOptionsContainer}`,
          clickOutsideDeactivates: () => closeUserOptions(),
          allowOutsideClick: true,
        }}
      >
        <div className={cx} ref={ref} data-component={componentName}>
          <div
            className={styles.userOptionsMenuButton}
            aria-label={translateMessage({
              id: 'media-ui-nav_arialabel_userAccountMenuButton',
              defaultMessage: 'User Account Menu Button',
            })}
          >
            <UserOptionsButton
              text={firstName || DEFAULT_USER_OPTIONS_BUTTON_TEXT}
              ref={setUserOptionsButton}
              onClick={() => onUserButtonClick()}
              icon={
                <UserAccountProfile className={styles.userAccountProfile} />
              }
            >
              {isDropdownOpen ? (
                <Triangle />
              ) : (
                <Triangle className={styles.triangleDown} />
              )}
            </UserOptionsButton>
          </div>
          {tips?.corporateHub && (
            <Popover
              className={styles.corporateHubTip}
              Icon={tips.corporateHub.icon}
              eyebrow={tips.corporateHub.eyebrow}
              anchorElement={userOptionsButton}
              allowedPlacements={tips.corporateHub.placement}
              onDismiss={tips.corporateHub.onDismiss}
              text={tips.corporateHub.text}
            />
          )}
          {isDropdownOpen && (
            <div
              className={styles.dropdown}
              aria-label={translateMessage({
                id: 'media-ui-nav_arialabel_userAccountMenu',
                defaultMessage: 'User Account Menu',
              })}
            >
              <UserOptionsDropdown
                sections={getLocalizedMenuSections(
                  getDropdownSections(),
                  translateMessage
                )}
                onSelect={onUserOptionSelect}
                onClose={() => onUserButtonClick()}
                onSignOut={onSignOut}
                theme={theme}
              />
            </div>
          )}
        </div>
      </FocusTrap>
    );
  }
);
